import { Controller } from "stimulus"
import SlimSelect from "slim-select"

// Keys that open the list from the widget, as a combobox is expected to.
const OPENING_KEYS = ["Enter", " ", "ArrowDown"]

// Shows a native <select multiple> as a searchable slim-select widget whose
// choices can all be dropped at once. Without it a filter over every member of
// staff is a wall of checkboxes or an unsearchable list box; with it each
// choice is a chip, and Clear puts the field back to "no one in particular".
//
// The Clear button is rendered hidden and disabled, so a page without
// JavaScript shows the plain list box and no control that cannot work. It is
// revealed once the widget is running and enabled only while something is
// chosen.
//
// slim-select v1 writes every change back to the native select and announces
// it with a bubbling `change` event (slimselect.js:1188-1190), so the Clear
// button's state is kept by listening to the select rather than to the widget.
//
// Everything about reaching this field from a keyboard is this controller's
// doing. slim-select takes the native select out of the tab order and out of
// the accessibility tree (`tabIndex = -1`, `aria-hidden="true"`,
// slimselect.js:1192-1197) and puts back a widget carrying class names and
// nothing else (:1344-1351): no tabindex, no role, no keydown. The one tabbable
// thing it builds is the search input (:1584), which lives inside the dropdown
// and so cannot be reached until something opens it. Left alone the field is a
// dead end, where the checkboxes it replaced were every one of them tabbable.
//
// Usage:
//   <div data-controller="searchable-multi-select"
//        data-searchable-multi-select-placeholder="Anyone">
//     <label for="internal_party_ids">Internal party</label>
//     <select id="internal_party_ids" name="internal_party_ids[]" multiple
//             data-target="searchable-multi-select.select"
//             data-action="change->searchable-multi-select#refreshClear">
//       <option value="4">Heather Graham</option>
//     </select>
//     <button type="button" hidden disabled
//             data-target="searchable-multi-select.clear"
//             data-action="searchable-multi-select#clear">Clear</button>
//   </div>
//
// The <label for> and any aria-describedby on the select are what name the
// widget, so give the select an id and label it as you would any other field.
export default class extends Controller {
  static targets = ["select", "clear"]

  connect() {
    // A Turbo cache snapshot may already contain slim-select markup from a
    // previous visit (the snapshot is taken before disconnect runs).
    this.removeStrayWidgets()

    this.slimSelect = new SlimSelect(this.slimSelectOptions())
    this.makeWidgetKeyboardOperable()
    this.revealClear()

    // Tear down before Turbo snapshots the page so the cached DOM holds the
    // pristine native select instead of the generated widget.
    this.beforeCache = () => this.teardown()
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  // The slim-select configuration. Subclasses add to it (an ajax search, say)
  // and keep everything this controller does to make the widget operable.
  slimSelectOptions() {
    return {
      select: this.selectTarget,
      placeholder: this.data.get("placeholder") || "",
      afterOpen: () => this.listOpened(),
      afterClose: () => this.listClosed()
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.teardown()
  }

  clear() {
    this.slimSelect?.set([])
  }

  refreshClear() {
    if (!this.hasClearTarget) return

    this.clearTarget.disabled = this.chosen().length === 0
  }

  revealClear() {
    if (!this.hasClearTarget) return

    this.clearTarget.hidden = false
    this.refreshClear()
  }

  // Put the button back the way the page rendered it. Teardown leaves a plain
  // list box behind — and the Turbo snapshot taken straight afterwards would
  // otherwise cache a visible Clear with no widget and no controller to answer
  // it, which is the control-that-cannot-work this was written to avoid.
  hideClear() {
    if (!this.hasClearTarget) return

    this.clearTarget.hidden = true
    this.clearTarget.disabled = true
  }

  chosen() {
    return Array.from(this.selectTarget.selectedOptions)
  }

  // Gives the widget the tab stop and the role the native select gave up, and
  // labels each chip's ✕ as it appears: slim-select v1 builds those as <span>s
  // with only an onclick handler (slimselect.js:1525-1553), afresh as the
  // selection changes (:1464-1518).
  makeWidgetKeyboardOperable() {
    const widget = this.widget()
    if (!widget) return

    widget.setAttribute("tabindex", "0")
    widget.setAttribute("role", "combobox")
    widget.setAttribute("aria-haspopup", "listbox")
    widget.setAttribute("aria-expanded", "false")
    this.nameWidget(widget)

    this.labelChipRemovals(widget)
    this.chipObserver = new MutationObserver(() => this.labelChipRemovals(widget))
    this.chipObserver.observe(widget, { childList: true, subtree: true })
    widget.addEventListener("keydown", (event) => this.keydown(event))
  }

  // A focusable thing that announces nothing is no better than an unreachable
  // one, so the widget answers to the field's own label and help text. They sit
  // on the native select, which slim-select has just marked aria-hidden.
  nameWidget(widget) {
    const id = this.selectTarget.id
    const label = id && this.element.querySelector(`label[for="${id}"]`)
    if (label) {
      if (!label.id) label.id = `${id}_label`
      widget.setAttribute("aria-labelledby", label.id)
    }

    const describedBy = this.selectTarget.getAttribute("aria-describedby")
    if (describedBy) widget.setAttribute("aria-describedby", describedBy)
  }

  // Keys the widget itself answers to. Anything raised inside the open dropdown
  // is slim-select's own to handle (its search input already walks the list),
  // and a chip's ✕ has its own meaning for the same keys.
  keydown(event) {
    if (event.target.classList.contains("ss-value-delete")) return this.chipRemovalKeydown(event)
    if (event.target !== this.widget()) return

    if (OPENING_KEYS.includes(event.key)) {
      // Space and ArrowDown would otherwise scroll the page.
      event.preventDefault()
      this.openedByKeyboard = true
      this.slimSelect?.open()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.slimSelect?.close()
    }
  }

  listOpened() {
    this.widget()?.setAttribute("aria-expanded", "true")
  }

  // Closing blurs the search input (slimselect.js:881) and puts focus nowhere,
  // which would drop a keyboard user back to the top of the page. Focus is
  // only taken back when the keyboard is what opened the list AND the close
  // left focus nowhere to go.
  //
  // That second condition is not belt and braces. slim-select defers this
  // callback by timeoutDelay (:878-885, :1055 — 200ms), so by the time it runs
  // a click elsewhere (documentClick, :598-602) or a Tab out of the search
  // input (:1606-1611) has already put focus where the user asked for it.
  // Taking it back then pulls them out of the field they just moved to and
  // swallows whatever they typed in the meantime.
  listClosed() {
    const widget = this.widget()
    if (!widget) return

    widget.setAttribute("aria-expanded", "false")
    if (this.openedByKeyboard && this.focusIsOurs(widget)) widget.focus()
    this.openedByKeyboard = false
  }

  // Nobody else has claimed focus: either nothing holds it, or the blur that
  // closing performs has just dropped it onto the body, or it is still inside
  // the widget we are about to restore it to.
  focusIsOurs(widget) {
    const active = document.activeElement
    return !active || active === document.body || widget.contains(active)
  }

  labelChipRemovals(widget) {
    widget.querySelectorAll(".ss-value-delete:not([role])").forEach((removal) => {
      const chosen = removal.closest(".ss-value")?.querySelector(".ss-value-text")?.textContent?.trim()
      removal.setAttribute("tabindex", "0")
      removal.setAttribute("role", "button")
      // A chip with nothing written on it is announced as "Remove", not as
      // "Remove undefined".
      removal.setAttribute("aria-label", chosen ? `Remove ${chosen}` : "Remove")
    })
  }

  chipRemovalKeydown(event) {
    if (!event.target.classList.contains("ss-value-delete")) return
    if (event.key !== "Enter" && event.key !== " ") return

    // Space would otherwise scroll the page.
    event.preventDefault()
    event.stopPropagation()
    event.target.click()
  }

  widget() {
    return this.selectTarget.parentNode?.querySelector(".ss-main")
  }

  teardown() {
    this.chipObserver?.disconnect()
    this.chipObserver = null
    this.hideClear()
    if (this.slimSelect) {
      try {
        this.slimSelect.destroy()
      } catch (error) {
        // The widget's DOM may already be gone on Turbo teardown.
      }
      this.slimSelect = null
    }
    this.removeStrayWidgets()
  }

  removeStrayWidgets() {
    if (!this.hasSelectTarget) return

    const parent = this.selectTarget.parentNode
    if (!parent) return

    parent.querySelectorAll(".ss-main").forEach((widget) => widget.remove())
    this.restoreSelect()
  }

  // slim-select's own destroy puts back `display` and `data-ssid` but leaves
  // the select aria-hidden and out of the tab order (slimselect.js:1003-1008),
  // so a Turbo snapshot taken after teardown would cache a field no keyboard
  // can reach until this controller happens to reconnect.
  restoreSelect() {
    this.selectTarget.style.display = ""
    this.selectTarget.removeAttribute("aria-hidden")
    this.selectTarget.removeAttribute("tabindex")
  }
}
